import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Lead, LeadListQuery, LeadSortField, LeadStatus, Pipeline } from '@neuroedge/shared';
import { PageState } from '../../components/ui';
import {
  DataGrid,
  dayBoundary,
  numberFilter,
  useGridState,
  type GridColumn,
  type GridState,
} from '../../components/data-grid/DataGrid';
import { useLeadCounts, useLeads } from '../../api/leads';
import { TagChips } from '../tags/TagChips';

const STATUSES: { value: LeadStatus; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'HOLD', label: 'Hold' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
  { value: 'ABANDONED', label: 'Abandoned' },
];

type Tab = 'ALL' | LeadStatus;

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

function statusTone(status: LeadStatus): string | undefined {
  if (status === 'WON') return 'var(--color-success)';
  if (status === 'LOST' || status === 'ABANDONED') return 'var(--color-danger)';
  return undefined;
}

function toListQuery(
  tab: Tab,
  g: GridState,
  scope: { pipelineId?: string; tagId?: string },
): LeadListQuery {
  const f = g.filters;
  return {
    page: g.page,
    pageSize: g.pageSize,
    sortBy: g.sortBy as LeadSortField,
    sortOrder: g.sortOrder,
    status: tab === 'ALL' ? undefined : tab,
    pipelineId: scope.pipelineId,
    tagId: scope.tagId,
    search: f.title?.trim() || undefined,
    contact: f.contactName?.trim() || undefined,
    stageId: f.stage || undefined,
    valueMin: numberFilter(f.valueMin),
    valueMax: numberFilter(f.valueMax),
    createdFrom: dayBoundary(f.createdAtFrom, false),
    createdTo: dayBoundary(f.createdAtTo, true),
  };
}

/**
 * Leads as a list on the standard `DataGrid` - the alternative to the
 * pipeline board. Status tabs with counts; filters for title, customer
 * (name/phone), stage, value and created date; sorting; paging. The
 * title opens the same lead detail popup the board uses.
 */
export function LeadsGrid({
  pipeline,
  tagId,
  onOpen,
}: {
  pipeline: Pipeline | undefined;
  tagId: string | undefined;
  onOpen: (leadId: string) => void;
}) {
  const [tab, setTab] = useState<Tab>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'updatedAt', sortOrder: 'desc' });
  const query = toListQuery(tab, grid, { pipelineId: pipeline?.id, tagId });
  const leads = useLeads(query);
  const counts = useLeadCounts(query);

  const columns: GridColumn<Lead>[] = [
    {
      key: 'title',
      header: 'Lead',
      sortable: true,
      filter: { type: 'text' },
      render: (l) => (
        <button
          type="button"
          onClick={() => onOpen(l.id)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            fontWeight: 600,
            color: 'var(--color-primary)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          {l.title}
        </button>
      ),
    },
    {
      key: 'contactName',
      header: 'Customer',
      sortable: true,
      filter: { type: 'text', placeholder: 'Name or phone' },
      render: (l) => (
        <>
          <Link to={`/contacts/${l.contact.id}`}>{l.contact.displayName}</Link>
          {l.contact.phone ? <div style={muted}>{l.contact.phone}</div> : null}
        </>
      ),
    },
    {
      key: 'stage',
      header: 'Stage',
      sortable: true,
      filter: pipeline
        ? { type: 'select', options: pipeline.stages.map((s) => ({ value: s.id, label: s.name })) }
        : undefined,
      render: (l) => l.stage.name,
    },
    {
      key: 'value',
      header: 'Value',
      sortable: true,
      align: 'right',
      filter: { type: 'range' },
      render: (l) => (l.valueAmount != null ? `${l.currency} ${l.valueAmount}` : '—'),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (l) => (
        <span className="ne-badge" style={{ color: statusTone(l.status) }}>
          {l.status}
        </span>
      ),
    },
    {
      key: 'assignee',
      header: 'Assigned to',
      render: (l) => l.assignedUser?.displayName ?? '—',
    },
    {
      key: 'tags',
      header: 'Tags',
      render: (l) => <TagChips tags={l.tags} empty={<span>—</span>} />,
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (l) => new Date(l.createdAt).toLocaleDateString(),
    },
  ];

  const tabs: { id: Tab; label: string; count: number | undefined }[] = [
    { id: 'ALL', label: 'All', count: counts.data?.all },
    ...STATUSES.map((s) => ({ id: s.value as Tab, label: s.label, count: counts.data?.[s.value] })),
  ];

  return (
    <div>
      <div role="tablist" aria-label="Lead status" className="ne-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className="ne-tabs__tab"
            onClick={() => {
              setTab(t.id);
              setGrid({ ...grid, page: 1 });
            }}
          >
            {t.label}
            {t.count !== undefined ? <span className="ne-tabs__count">{t.count}</span> : null}
          </button>
        ))}
      </div>
      <div style={{ marginTop: 'var(--space-3)' }}>
        {leads.isError ? (
          <PageState
            kind="error"
            message={leads.error.message}
            onRetry={() => void leads.refetch()}
          />
        ) : (
          <DataGrid
            columns={columns}
            rows={leads.data?.items ?? []}
            rowKey={(l) => l.id}
            rowLabel={(l) => l.title}
            total={leads.data?.meta.total ?? 0}
            state={grid}
            onStateChange={setGrid}
            loading={leads.isFetching}
            emptyMessage={
              Object.keys(grid.filters).length > 0 || tab !== 'ALL'
                ? 'No leads match these filters.'
                : 'No leads in this pipeline yet.'
            }
          />
        )}
      </div>
    </div>
  );
}
